import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, Search } from "lucide-react";
import api from "@/lib/api";
import { LEAD_STAGES, LEAD_STAGE_LABELS } from "@/lib/constants";

const stageColor = {
  NOVO: "#A1A1AA", QUALIFICANDO: "#007AFF", DIMENSIONAMENTO: "#007AFF",
  PROPOSTA: "#eab308", NEGOCIACAO: "#FF5500", GANHO: "#22c55e", PERDIDO: "#ef4444",
};

export default function LeadsAdmin() {
  const [leads, setLeads] = useState([]);
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    await api.patch(`/leads/${sel.id}`, {
      status: sel.status, potential_value: sel.potential_value ? Number(sel.potential_value) : null,
      assigned_to: sel.assigned_to, next_action: sel.next_action, notes: sel.notes,
    });
    toast.success("Lead atualizado");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este lead?")) return;
    await api.delete(`/leads/${id}`);
    setSel(null); load(); toast.success("Lead excluído");
  };

  const filtered = leads.filter((l) => (filter === "all" || l.status === filter) &&
    (l.name + l.company + l.equipment_type + l.city).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-white">Leads / CRM</h1>
      <p className="mt-1 text-sm text-[#A1A1AA]">Gerencie a inteligência comercial: estágio, valor potencial, responsável e próxima ação.</p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#A1A1AA]" />
          <Input data-testid="leads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="w-64 rounded-none border-[#27272A] bg-[#151618] pl-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="leads-filter" className="w-48 rounded-none border-[#27272A] bg-[#151618]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#151618] text-white border-[#27272A]">
            <SelectItem value="all">Todos os estágios</SelectItem>
            {LEAD_STAGES.map((s) => <SelectItem key={s} value={s}>{LEAD_STAGE_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-[#A1A1AA]">{filtered.length} lead(s)</span>
      </div>

      <div className="mt-4 overflow-x-auto border border-[#27272A] bg-[#151618]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#27272A] text-left text-[0.7rem] uppercase tracking-wider text-[#A1A1AA]">
              <th className="px-4 py-3">Nome</th><th className="px-4 py-3">Empresa</th><th className="px-4 py-3">Equipamento</th>
              <th className="px-4 py-3">Local</th><th className="px-4 py-3">Telefone</th><th className="px-4 py-3">Estágio</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`} onClick={() => setSel({ ...l })} className="cursor-pointer border-b border-[#27272A] last:border-0 hover:bg-[#1E1F22]">
                <td className="px-4 py-3 font-medium text-white">{l.name}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{l.company || "—"}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{l.equipment_type || "—"}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{l.city} {l.state}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{l.phone}</td>
                <td className="px-4 py-3"><span className="border px-2 py-1 text-xs font-bold" style={{ color: stageColor[l.status], borderColor: stageColor[l.status] }}>{LEAD_STAGE_LABELS[l.status]}</span></td>
                <td className="px-4 py-3"><button onClick={(e) => { e.stopPropagation(); del(l.id); }} data-testid={`lead-delete-${l.id}`}><Trash2 className="h-4 w-4 text-[#71717A] hover:text-red-400" /></button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#A1A1AA]">Nenhum lead encontrado.</td></tr>}
          </tbody>
        </table>
      </div>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full overflow-y-auto border-[#27272A] bg-[#151618] text-white sm:max-w-md" data-testid="lead-detail-sheet">
          {sel && (
            <>
              <SheetHeader><SheetTitle className="font-heading text-xl text-white">{sel.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3 border border-[#27272A] p-3 text-[#A1A1AA]">
                  <div><span className="text-[#71717A]">Empresa:</span> {sel.company || "—"}</div>
                  <div><span className="text-[#71717A]">Telefone:</span> {sel.phone}</div>
                  <div><span className="text-[#71717A]">E-mail:</span> {sel.email || "—"}</div>
                  <div><span className="text-[#71717A]">Local:</span> {sel.city} {sel.state}</div>
                  <div><span className="text-[#71717A]">Equipamento:</span> {sel.equipment_type || "—"}</div>
                  <div><span className="text-[#71717A]">Aplicação:</span> {sel.application || "—"}</div>
                  <div><span className="text-[#71717A]">Capacidade:</span> {sel.capacity || "—"}</div>
                  <div><span className="text-[#71717A]">Temperatura:</span> {sel.temperature || "—"}</div>
                  <div className="col-span-2"><span className="text-[#71717A]">Dimensões:</span> {sel.dimensions || "—"}</div>
                  <div className="col-span-2"><span className="text-[#71717A]">Origem:</span> {sel.source}</div>
                  {sel.message && <div className="col-span-2"><span className="text-[#71717A]">Mensagem:</span> {sel.message}</div>}
                  {sel.attachments?.length > 0 && (
                    <div className="col-span-2"><span className="text-[#71717A]">Anexos:</span>
                      <div className="mt-1 flex flex-col gap-1">
                        {sel.attachments.map((a, i) => (
                          <a key={i} href={a} target="_blank" rel="noopener noreferrer" data-testid={`lead-attachment-${i}`} className="truncate text-[#FF5500] hover:underline">Anexo {i + 1}</a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div><Label className="label-eyebrow">Estágio</Label>
                  <Select value={sel.status} onValueChange={(v) => setSel({ ...sel, status: v })}>
                    <SelectTrigger data-testid="lead-stage-select" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#151618] text-white border-[#27272A]">{LEAD_STAGES.map((s) => <SelectItem key={s} value={s}>{LEAD_STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="label-eyebrow">Valor potencial (R$)</Label><Input data-testid="lead-value" type="number" value={sel.potential_value || ""} onChange={(e) => setSel({ ...sel, potential_value: e.target.value })} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
                <div><Label className="label-eyebrow">Responsável</Label><Input data-testid="lead-owner" value={sel.assigned_to || ""} onChange={(e) => setSel({ ...sel, assigned_to: e.target.value })} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
                <div><Label className="label-eyebrow">Próxima ação</Label><Input data-testid="lead-next-action" value={sel.next_action || ""} onChange={(e) => setSel({ ...sel, next_action: e.target.value })} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
                <div><Label className="label-eyebrow">Anotações</Label><Textarea data-testid="lead-notes" value={sel.notes || ""} onChange={(e) => setSel({ ...sel, notes: e.target.value })} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
                <Button data-testid="lead-save" onClick={save} className="w-full rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]">Salvar</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
